import { Clapperboard, FileText, Film, MapPin, Package, Users, Video, type LucideIcon } from "lucide-react"
import type { TaskAgent, TaskAgentShot, TaskAssetKind, TaskAssetLink } from "@/types/tasks"

// Журнал ассета без человека подписывается ассистентом этого типа.
const ASSET_ACTIVITY_AGENT: Record<TaskAssetKind, TaskAgent> = {
  character: "characters",
  location: "locations",
  prop: "props",
  shot: "shot",
  script: "script",
}

export function activityAgentForAsset(kind: TaskAssetKind | null, fallback: TaskAgent): TaskAgent {
  return kind ? ASSET_ACTIVITY_AGENT[kind] : fallback
}

// Кому можно передать задачу (docs/task_agent_delegation_plan.md). Порядок — порядок в селекторе.
export const TASK_AGENTS: readonly TaskAgent[] = ["project", "characters", "locations", "props", "script", "shot"]

// Меню агента в панели: без ассета — все, с ассетом — ассистент проекта и ассистент этого типа.
export function agentsForAsset(kind: TaskAssetKind | null): readonly TaskAgent[] {
  if (kind == null) return TASK_AGENTS
  return ["project", ASSET_ACTIVITY_AGENT[kind]]
}

// Подстановка агента в момент выбора ассета.
// «Нет» и шот без id ставят ассистента проекта: ассистенту шота нужен конкретный шот.
// Герой, локация и реквизит без id ставят ассистента типа.
export function agentChoiceForAsset(
  kind: TaskAssetKind | null,
  asset: TaskAssetLink | null,
): { agent: TaskAgent; agent_shot_id: number | null; shot: TaskAgentShot | null } {
  if (kind == null || (kind === "shot" && asset == null)) return { agent: "project", agent_shot_id: null, shot: null }
  const agent = ASSET_ACTIVITY_AGENT[kind]
  if (asset == null || agent !== "shot") return { agent, agent_shot_id: null, shot: null }
  return {
    agent,
    agent_shot_id: asset.id,
    shot: { id: asset.id, label: asset.label, thumb_url: asset.thumb_url ?? null },
  }
}

export const TASK_AGENT_ICON: Record<TaskAgent, LucideIcon> = {
  project: Clapperboard,
  shot: Video,
  characters: Users,
  locations: MapPin,
  props: Package,
  script: FileText,
}

// Тип сущности на карточке и в меню ассета. У шота значок кадра.
export const TASK_ASSET_ICON: Record<TaskAssetKind, LucideIcon> = {
  character: TASK_AGENT_ICON.characters,
  location: TASK_AGENT_ICON.locations,
  prop: TASK_AGENT_ICON.props,
  shot: Film,
  script: TASK_AGENT_ICON.script,
}

// Ключ названия в неймспейсе workspace. Ассистент проекта — та же строка, что в шапке его чата;
// остальные — названия из плашек handoff.
export function taskAgentLabelKey(agent: TaskAgent) {
  return agent === "project" ? ("agent.title" as const) : (`agent.handoff.surface.${agent}` as const)
}

// Ключ названия в шапке чата: по умолчанию чат работает в режиме ассистента шотов.
export function chatAgentLabelKey(mode: string | undefined, contextType: string) {
  return taskAgentLabelKey(taskAgentForChat(mode ?? "shots_assistant", contextType))
}

// Агент чата по его режиму и контексту: название в шапке чата — та же строка, что в селекторе задачи.
// Прочие чаты (холст, скиллы) остаются под общим названием ассистента проекта.
export function taskAgentForChat(mode: string, contextType: string): TaskAgent {
  switch (mode) {
    case "shots_assistant":
      return contextType === "shot" ? "shot" : "project"
    case "characters_assistant":
      return "characters"
    case "locations_assistant":
      return "locations"
    case "props_assistant":
      return "props"
    case "script_assistant":
      return "script"
    default:
      return "project"
  }
}

export function taskAgentShotLabel(
  season: number | null | undefined,
  episode: number,
  scene: number,
  shot: string,
): string {
  const code = `${scene}.${shot}`
  return season == null ? code : `S${season}E${episode} — ${code}`
}

// «S1E1 — 1.1». Название ассета «S1E1 Шот 1.1» и старые коды приводим к тому же виду.
export function taskAgentShotCode(label: string): string {
  return label
    .replace(/^(S\d+E\d+) Шот /, "$1 — ")
    .replace(/^(S\d+E\d+) - /, "$1 — ")
    .replace(/^(S\d+E\d+) (\d)/, "$1 — $2")
}
